import React, { useEffect, useMemo } from 'react'
import log from 'loglevel'
import utf8 from 'utf8'
import isString from 'lodash/isString'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { useGoogleDriveContext } from 'src/lib/Linkers/context/GoogleDriveContext'
import { useConfluenceFilePickerContext } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerContext'
import { useDirectPickerSelection } from 'src/components/DocumentPicker'
import { useLoadGoogleApi } from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.hooks'
import { useInvalidateEvidence } from 'src/Utils/swrCacheInvalidation'
import {
  useAlertDialogModal,
  ShowAlertDialog,
} from 'src/components/ModalForms/useAlertDialogModal'
import { useSharepointLinkerShareUrl } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerModal.hooks'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { PendingAttachment } from 'src/pages/Requests/RequestShowPage/components/Comments/CommentAttachment.types'
import { ModalForm, ModalFormId, ModalFormIdQueryParam } from '../../ModalForm'
import { useCreateEvidence } from '../../../async/suggestions/useCreateEvidence'
import { useBulkCreateEvidence } from '../../../async/evidence/useBulkCreateEvidence'
import {
  EvidenceFormData,
  EvidenceType,
} from './forms/AddEvidenceForm.constants'
import { AddEvidenceForm } from './forms/AddEvidenceForm'
import { AddEvidenceFormDescription } from './forms/AddEvidenceForm.components'
import {
  ReceptorFileUploadTarget,
  useUploadReceptorFile,
} from './AddEvidenceModal.hooks'

export const AddEvidenceQueryParams = {
  MODAL_FORM_ID: ModalFormIdQueryParam,
  CONTROL_ID: 'controlId',
  REQUEST_ID: 'requestId',
}

type AddEvidenceModalProps = {
  controlId?: string
  requestId?: string
  name: string
  openOnDrag?: boolean
  show?: boolean
  onHide?: () => void
  onCollect?: (attachment: PendingAttachment) => void
  initialFile?: File
  receptorFileUpload?: ReceptorFileUploadTarget
} & (
  | { requestId: string; controlId?: never }
  | { controlId: string; requestId?: never }
)
/**
 * Modal form to add evidence
 */
export const AddEvidenceModal = ({
  controlId,
  requestId,
  name,
  openOnDrag = false,
  show: showProp,
  onHide: onHideProp,
  onCollect,
  initialFile,
  receptorFileUpload,
}: AddEvidenceModalProps): JSX.Element => {
  const runSharepointLinker = useSharepointLinkerShareUrl()
  const googleReady = useLoadGoogleApi(true)
  const showAlerDialog: ShowAlertDialog = useAlertDialogModal()

  // SharePoint
  const {
    setFilesSelected: setSharepointFilesSelected,
    filesSelected: sharepointFilesSelected,
    onSubmit: onSharepointSubmit,
  } = useSharepointFilePickerContext()

  // Google Drive
  const {
    filesSelected: gdriveFilesSelected,
    setFilesSelected: setGDriveFileSelected,
    setPickerConfig,
    setSource: setGDriveSource,
    setSubjectIds: setGDriveSubjectIds,
    onSubmit: onGDriveSubmit,
  } = useGoogleDriveContext()

  // Confluence
  const {
    filesSelected: confluenceFilesSelected,
    setFilesSelected: setConfluenceFilesSelected,
    onSubmit: onConfluenceSubmit,
    setSource: setConfluenceSource,
    setSubjectIds: setConfluenceSubjectIds,
  } = useConfluenceFilePickerContext()

  // Document picker selections with no legacy context (Notion)
  const {
    nodes: pickedNodes,
    clear: clearPickedNodes,
    link: linkPickedNodes,
  } = useDirectPickerSelection('Evidence')

  const isShowFromContext = useIsShowModal(ModalFormId.ADD_EVIDENCE)
  const hideFromContext = useHideModal({ modalId: ModalFormId.ADD_EVIDENCE })
  const show = showProp ?? isShowFromContext
  const hide = onHideProp
    ? () => {
        onHideProp()
      }
    : hideFromContext

  const createEvidence = useCreateEvidence({
    controlId,
    requestId,
  })

  const bulkCreateParams = useMemo(() => {
    if (controlId) {
      return { controlId: [controlId] }
    }
    if (requestId) {
      return { requestId: [requestId] }
    }
    return {}
  }, [controlId, requestId])

  const bulkCreateEvidence = useBulkCreateEvidence(bulkCreateParams)
  const uploadReceptorFile = useUploadReceptorFile()
  const invalidateEvidence = useInvalidateEvidence()

  useEffect(() => {
    if (googleReady) {
      setPickerConfig({
        allowMultiselect: true,
        modalId: ModalFormId.ADD_EVIDENCE,
      })
    } else {
      setPickerConfig(null)
    }
  }, [googleReady, setPickerConfig])

  useEffect(() => {
    // Keep the various pickers aligned with the parent modal context.
    const subjectIds: string[] = []
    if (requestId) {
      subjectIds.push(requestId)
    } else if (controlId) {
      subjectIds.push(controlId)
    }

    setGDriveSource('Evidence')
    setGDriveSubjectIds(subjectIds)

    // Confluence mirrors the same pattern as SharePoint / GDrive.
    setConfluenceSource('Evidence')
    setConfluenceSubjectIds(subjectIds)
  }, [
    setGDriveSource,
    setGDriveSubjectIds,
    setConfluenceSource,
    setConfluenceSubjectIds,
    controlId,
    requestId,
  ])

  const hideHandler = () => {
    setSharepointFilesSelected([])
    setGDriveFileSelected?.([])
    setConfluenceFilesSelected([])
    clearPickedNodes()
    hide()
  }

  const onSubmitFunc = async (
    caption: string,
    formData: EvidenceFormData,
    formDataList: EvidenceFormData[],
    date: Timestamp,
    type: EvidenceType,
    uploadSize = 0,
  ) => {
    let hasError = false
    try {
      if (type === EvidenceType.FILE) {
        const singleFile = formDataList.length === 1 ? formDataList[0] : null
        if (onCollect && singleFile?.body instanceof File) {
          onCollect({
            kind: 'evidence',
            body: singleFile.body,
            mime: singleFile.mime,
            caption,
            relevantDate: date,
          })
          hideHandler()
          return
        }
        await bulkCreateEvidence(
          formDataList.map((f) => f.body as File),
          date,
          uploadSize,
          caption,
        )
        if (receptorFileUpload && controlId) {
          await Promise.all(
            formDataList.map(async ({ body: sourceFile }) => {
              if (!(sourceFile instanceof File)) return
              try {
                const body = new Uint8Array(
                  await new Response(sourceFile).arrayBuffer(),
                )
                await uploadReceptorFile({
                  receptorId: receptorFileUpload.receptorId,
                  uploadTargetKey: receptorFileUpload.uploadTargetKey,
                  recordFields: receptorFileUpload.recordFields,
                  body,
                  fileName: sourceFile.name,
                  mimeType: sourceFile.type,
                })
              } catch (error) {
                log.warn('Receptor file upload failed', error)
              }
            }),
          )
        }
      } else if (type === EvidenceType.SHAREPOINTLINK) {
        await runSharepointLinker(
          formData.body.toString(),
          DOCUMENT_TYPE.EVIDENCE,
          !!requestId,
          caption,
          requestId ? [requestId] : controlId ? [controlId] : [],
        )
        await invalidateEvidence()
      } else if (pickedNodes.length) {
        await linkPickedNodes({
          caption,
          isViaRequest: !!requestId,
          relevantDate: date,
          subjectIds: requestId ? [requestId] : controlId ? [controlId] : [],
        })
        await invalidateEvidence()
      } else if ((confluenceFilesSelected || []).length) {
        setConfluenceSubjectIds(
          requestId ? [requestId] : controlId ? [controlId] : [],
        )
        await onConfluenceSubmit({
          caption,
          isViaRequest: !!requestId,
          relevantDate: date,
          // Multi-select Confluence submission
          node: confluenceFilesSelected[0],
          nodes: confluenceFilesSelected,
        })
        await invalidateEvidence()
      } else if (sharepointFilesSelected.length) {
        await onSharepointSubmit({
          caption,
          isViaRequest: !!requestId,
          relevantDate: date,
        })
        await invalidateEvidence()
      } else if ((gdriveFilesSelected || []).length) {
        await onGDriveSubmit({
          caption,
          isViaRequest: !!requestId,
          relevantDate: date,
        })
        await invalidateEvidence()
      } else {
        const { mime, body } = formData
        if (onCollect && isString(body)) {
          onCollect({
            kind: 'evidence',
            body: body.trim(),
            mime,
            caption,
            relevantDate: date,
          })
          hideHandler()
          return
        }
        // Convert string into utf8 encoded codes
        const bodyBytes = new Uint8Array(
          isString(body)
            ? Array.from(utf8.encode(body.trim())).map((s) => s.charCodeAt(0))
            : [],
        )
        const fileName = formData.body instanceof File ? formData.body.name : ''
        // If this fails the user will get a toast
        await createEvidence({
          mime,
          bodyBytes,
          caption: `${caption.trim()}`,
          relevantDate: date,
          fileName,
        })
      }
    } catch (error) {
      hasError = true
      log.error('Error adding evidence', error)
      if (error instanceof Error) {
        showInfoToast(error.message)
      } else {
        showInfoToast('Error adding evidence')
      }
    } finally {
      hideHandler()

      const showUploadError = () => {
        showAlerDialog({
          title: 'Unable to Add Evidence',
          body: 'We couldn’t upload your evidence. Please check your permissions or try again. If the problem continues, contact your administrator for help.',
        })
      }

      if (hasError && sharepointFilesSelected.length > 0) {
        showUploadError()
      }
      if (hasError && (gdriveFilesSelected || []).length) {
        showUploadError()
      }
      if (hasError && (confluenceFilesSelected || []).length) {
        showUploadError()
      }
      if (hasError && pickedNodes.length) {
        showUploadError()
      }
    }
  }

  const isControlled = showProp !== undefined

  return (
    <ModalForm
      formId={ModalFormId.ADD_EVIDENCE}
      title={`Add Evidence for ${name}`}
      description={<AddEvidenceFormDescription />}
      openOnDrag={openOnDrag}
      submitText={isControlled ? 'Use Evidence' : 'Add Evidence'}
      show={show}
      hide={hideHandler}
      enforceFocus={false}
    >
      <AddEvidenceForm
        onSubmitFunc={onSubmitFunc}
        controlId={controlId}
        modalId={isControlled ? undefined : ModalFormId.ADD_EVIDENCE}
        formId={ModalFormId.ADD_EVIDENCE}
        isShown={show}
        initialFile={initialFile}
      />
    </ModalForm>
  )
}
